"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 5: MMC5 (ExROM)
 *
 * The register map follows the MMC5 documentation: PRG banking in four modes
 * with ROM or work RAM windows, 64 KB of work RAM behind two write protect
 * registers, CHR banking in 8/4/2/1 KB pages through ten bit registers whose
 * top two bits are latched in $5130, the four modes of the 1 KB of extended
 * RAM, the per slot name table mapping of $5105 and the scanline IRQ.
 *
 * Left out, because no released game needs them: the vertical split screen of
 * $5200-$5202 (only Uchuu Keibitai SDF and Bandit Kings of Ancient China use
 * it) and the independent background banks that go with 8x16 sprites.
 * Castlevania III, the game this mapper is normally wanted for, uses none of
 * those.
 *
 * Implemented: the expansion sound (two 2A03-style pulse channels plus the
 * 8-bit PCM channel, see papu.js MMC5Sound) and extended attribute mode of
 * $5104 (%01), where every background tile takes its palette and its 4 KB
 * pattern bank from the extended RAM byte at its position -- what Just Breed,
 * Yakuman Tengoku and the Koei titles render with continuously.
 * Added: vertical split screen $5200-$5202 (Uchuu Keibitai SDF and Bandit Kings
 * of Ancient China).
 * ------------------------------------------------------------------ */
// MMC5 (the most complex Nintendo board)

function register(Mappers) {
  Mappers[5] = function (nes) {
    this.nes = nes;
  };

  Mappers[5].prototype = new Mappers[0]();

  Mappers[5].prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);

    // $5100: four 8 KB banks, the mode every game but Castlevania III leaves
    // selected.
    this.prgMode = 3;
    // $5101: 1 KB CHR pages, used by every game but Metal Slader Glory.
    this.chrMode = 3;

    // $5102 and $5103 gate every write to work RAM: they have to read %10 and %01.
    this.ramProtect1 = 0;
    this.ramProtect2 = 0;

    // $5104: the extended RAM is a plain name table source.
    this.exRamMode = 0;

    // $5105-$5107: name table mapping, fill mode tile and fill mode attribute.
    this.nametableMap = 0;
    this.fillTile = 0;
    this.fillAttrib = 0;

    // $5113-$5117. Games expect $FF (the last bank) at power on, which is what
    // makes the reset vector reachable.
    this.prgReg5113 = 0xff;
    this.prgReg5114 = 0xff;
    this.prgReg5115 = 0xff;
    this.prgReg5116 = 0xff;
    this.prgReg5117 = 0xff;

    // $5120-$512F latch ten bits: the byte written plus whatever $5130 held when
    // it was written. $5120-$5127 are the 'A' set, $5128-$512F the 'B' set.
    this.chrReg = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    this.chrUpper = 0;

    // Which set is mapped in: 'A' for 8x8 sprites, 'B' for the background when
    // sprites are 8x16. `watchesChrFetches` tells the PPU to call back between
    // the background and the sprite fetches.
    this.sprite8x16 = false;
    this.chrSet = 0;
    this.watchesChrFetches = false;

    // Vertical split screen $5200-$5202 (only Uchuu Keibitai SDF and Bandit Kings
    // of Ancient China use it).
    this.watchesSplitScreen = false; // true when $5200 bit 7 is set
    this.splitControl = 0;
    this.splitScroll = 0;
    this.splitBank = 0;
    this.splitViewReady = false;

    // $5203/$5204: scanline IRQ target, enable flag and status.
    this.irqTarget = 0;
    this.irqEnabled = false;
    this.irqPending = false;
    this.inFrame = false;
    this.scanlineCounter = 0;
    this.irqOnCpu = false;
    this.renderDisabled = true;

    // Load split bank tiles into splitTiles view
    this.loadSplitBank = function () {
      // Not needed: splitScreenFetch reads directly from ROM vromTile
      this.splitViewReady = true;
    };

    // $5205/$5206 multiplier.
    this.multA = 0;
    this.multB = 0;

    // The MMC5 drives the $2000-$2FFF layout itself, so the mirroring from the
    // cartridge header must not overwrite $5105.
    this.ownsMirroring = true;

    // 64 KB of work RAM and 1 KB of extended RAM. They survive a soft reset, so
    // they are only allocated the first time round.
    if (!this.prgRam) {
      this.prgRam = new Uint8Array(0x10000);
      this.exRam = new Uint8Array(0x400);
    }

    // One decoded view per CHR register set: the tile references the renderer
    // reads plus a byte image of $0000-$1FFF. Allocated once; updateChrBanks()
    // marks both stale whenever a register changes.
    if (!this.chrView) {
      this.chrView = [
        { tiles: new Array(512), mem: new Uint8Array(0x2000) },
        { tiles: new Array(512), mem: new Uint8Array(0x2000) },
        { tiles: new Array(512), mem: new Uint8Array(0x2000) },
      ];
    }
    this.chrViewReady = [false, false];
    this.chrVramSet = -1;

    this.updatePrgBanks();
    this.updateChrBanks();
    // Same reason as the two above: the PPU's own reset blanks ntable1 to
    // [null, null, null, null], so a soft reset that skipped this left the board
    // with no name table pages at all and every background fetch reading through
    // a null page.
    this.updateNametableMap();

    // The expansion sound module comes from nes.js, which hands it to this
    // mapper on cartridge load; a soft reset does not reload the cartridge,
    // so the module has to be silenced from here (mapper 19 does the same).
    if (this.sound) {
      this.sound.reset();
    }
  };

  Mappers[5].prototype.write = function (address, value) {
    if (address >= 0x5000 && address <= 0x5fff) {
      if (address >= 0x5c00) {
        this.writeExRam(address - 0x5c00, value);
      } else {
        this.writeRegister(address, value);
      }
      return;
    }

    if (address >= 0x6000) {
      this.writeWorkRam(address, value);
      return;
    }

    // The MMC5 watches the fully decoded PPUMASK register.
    if (address === 0x2001) {
      this.onMaskRegisterWrite(value);
    }

    // Sprite size lives in $2000, and it decides which CHR register set the
    // background is driven from.
    if (address === 0x2000) {
      this.onControlReg1Write(value);
    }

    Mappers[0].prototype.write.apply(this, arguments);
  };

  Mappers[5].prototype.load = function (address) {
    address &= 0xffff;

    if (address >= 0x5000 && address < 0x5c00) {
      // $5015 is the length status of the expansion sound channels and $5010
      // the PCM status/IRQ acknowledge; the other audio registers read back
      // as open bus.
      if ((address === 0x5010 || address === 0x5015) && this.sound) {
        return this.sound.statusRead(address);
      }
      return address >= 0x5204 && address <= 0x5206
        ? this.readRegister(address)
        : 0;
    }

    if (address >= 0x5c00 && address < 0x6000) {
      return this.readExRam(address - 0x5c00);
    }

    if (address >= 0x6000) {
      if (address === 0xfffa || address === 0xfffb) {
        // Reading a VBlank vector is how the MMC5 learns the frame is over.
        this.clearFrame();
      }
      if (
        this.sound &&
        this.sound.pcmReadMode &&
        address >= 0x8000 &&
        address <= 0xbfff
      ) {
        // PCM read mode ($5010 bit 0 set): every CPU read from the first
        // PRG window is a sample fetch, and the value read is the sample.
        var pcmValue = this.readPrg(address);
        this.sound.onPcmFetch(pcmValue);
        return pcmValue;
      }
      return this.readPrg(address);
    }

    return Mappers[0].prototype.load.apply(this, arguments);
  };

  Mappers[5].prototype.writeRegister = function (address, value) {
    switch (address) {
      case 0x5100:
        this.prgMode = value & 3;
        this.updatePrgBanks();
        break;
      case 0x5101:
        this.chrMode = value & 3;
        this.updateChrBanks();
        break;
      case 0x5102:
        this.ramProtect1 = value & 3;
        break;
      case 0x5103:
        this.ramProtect2 = value & 3;
        break;
      case 0x5104:
        this.exRamMode = value & 3;
        this.updateNametableMap();
        break;
      case 0x5105:
        this.nametableMap = value;
        this.updateNametableMap();
        break;
      case 0x5106:
        this.fillTile = value & 0xff;
        break;
      case 0x5107:
        this.fillAttrib = value & 3;
        break;

      case 0x5113:
        this.prgReg5113 = value & 0xff;
        this.updatePrgBanks();
        break;
      case 0x5114:
        this.prgReg5114 = value & 0xff;
        this.updatePrgBanks();
        break;
      case 0x5115:
        this.prgReg5115 = value & 0xff;
        this.updatePrgBanks();
        break;
      case 0x5116:
        this.prgReg5116 = value & 0xff;
        this.updatePrgBanks();
        break;
      case 0x5117:
        this.prgReg5117 = value & 0xff;
        this.updatePrgBanks();
        break;

      case 0x5130:
        // Latched into the CHR registers written after this one.
        this.chrUpper = value & 3;
        break;

      case 0x5200:
        this.splitControl = value & 0xff;
        // Split screen enables per-tile bank selection, which means the PPU must
        // call chrBankSelect between background and sprite fetches so the mapper
        // can substitute split-bank tiles for the split region.
        this.watchesSplitScreen = (value & 0x80) !== 0;
        this.watchesChrFetches = this.watchesSplitScreen || this.sprite8x16;
        break;
      case 0x5201:
        this.splitScroll = value & 0xff;
        break;
      case 0x5202:
        this.splitBank = value & 0xff;
        break;

      case 0x5203:
        this.irqTarget = value & 0xff;
        break;
      case 0x5204:
        this.irqEnabled = (value & 0x80) !== 0;
        this.refreshCpuIrq();
        break;

      case 0x5205:
        this.multA = value & 0xff;
        break;
      case 0x5206:
        this.multB = value & 0xff;
        break;

      default:
        if (address >= 0x5120 && address <= 0x512f) {
          var index = address - 0x5120;
          var bank = (value & 0xff) | ((this.chrUpper & 3) << 8);
          // Re-mapping 1 KB of CHR for an unchanged register is pure overhead, and
          // games rewrite the same bank every frame.
          if (this.chrReg[index] !== bank) {
            this.chrReg[index] = bank;
            this.updateChrBanks();
          }
        } else if (address <= 0x5015) {
          // $5000-$5015: the two extra pulse channels and the PCM channel.
          this.nes.papu.exWrite(address, value);
        }
        break;
    }
  };

  Mappers[5].prototype.readRegister = function (address) {
    switch (address) {
      case 0x5204:
        var status = (this.irqPending ? 0x80 : 0) | (this.inFrame ? 0x40 : 0);
        // Reading acknowledges the pending IRQ; the counter keeps running.
        this.irqPending = false;
        this.refreshCpuIrq();
        return status;
      case 0x5205:
        return (this.multA * this.multB) & 0xff;
      case 0x5206:
        return ((this.multA * this.multB) >> 8) & 0xff;
      default:
        return 0;
    }
  };

  // ---- the 1 KB of extended RAM, seen by the CPU at $5C00-$5FFF ----

  Mappers[5].prototype.writeExRam = function (offset, value) {
    if (this.exRamMode === 3) {
      // Mode %03 is read only.
      return;
    }
    if (this.exRamMode < 2 && !this.ppuIsRendering()) {
      // Modes %00 and %01 only accept a write while the PPU is rendering; outside
      // of rendering the write is dropped.
      return;
    }
    this.exRam[offset] = value;
  };

  Mappers[5].prototype.readExRam = function (offset) {
    // Modes %00 and %01 leave CPU reads as open bus.
    return this.exRamMode < 2 ? 0 : this.exRam[offset];
  };

  Mappers[5].prototype.ppuIsRendering = function () {
    var ppu = this.nes.ppu;
    return ppu.f_bgVisibility === 1 || ppu.f_spVisibility === 1;
  };

  // ---- vertical split screen ----

  // The split screen substitutes a separate 4 KB CHR bank ($5202) for tiles
  // past the threshold on the selected side. The split bank is mapped at both
  // $0000 and $1000, so the split tiles come from the same pattern table the
  // PPU is already reading; the bank number comes from $5202.
  //
  // The PPU calls this for every background tile fetch while the split is
  // enabled. A null return means "use the normal fetch".
  Mappers[5].prototype.splitScreenFetch = function (coarseX, tileIndex) {
    if (!this.watchesSplitScreen || this.renderDisabled) {
      return null;
    }
    var rom = this.nes.rom;
    if (!rom || rom.vromCount === 0) {
      return null;
    }

    // $5200: bit 7 enable, bit 6 side (0=left, 1=right), bits 4-0 threshold
    var control = this.splitControl;
    var side = (control >> 6) & 1;
    var threshold = control & 0x1f;
    var isInSplit = side === 1
      ? coarseX >= threshold
      : coarseX < threshold;

    if (!isInSplit) {
      return null;
    }

    // The split bank is a 4 KB bank; $5202's 6 low bits select it. The bank
    // is mapped at both $0000 and $1000, so the tile index is used as-is.
    var bank4k = this.splitBank & 0x3f;
    var vromTile = rom.vromTile[bank4k % rom.vromCount];
    return {
      tile: vromTile[tileIndex & 0xff],
      att: 0,
    };
  };

  // ---- extended attribute mode ----

  // $5104 = %01 turns every background pattern fetch into an extended one:
  // the extended RAM byte at the tile's position carries the 2-bit palette in
  // bits 7-6 and the 6 low bits of the 4 KB pattern bank in bits 5-0, with the
  // live $5130 as the top two bits of the bank. CHR mode and $5120-$512B are
  // ignored for these fetches, and the substitutions are off whenever the
  // PPU's rendering enable bits ($2001 bits 4/3) are both clear.
  //
  // The exRAM attribute map is 1-screen mirrored: one 32x30 map shared by all
  // four name tables, indexed by the position inside each one. The PPU asks
  // the board for every background tile while this mode is active, and here
  // is the answer it gets -- null falls back to the ordinary fetch.
  Mappers[5].prototype.extAttrFetch = function (coarseX, coarseY, tileIndex) {
    if (this.exRamMode !== 1 || this.renderDisabled) {
      return null;
    }
    var rom = this.nes.rom;
    if (!rom || rom.vromCount === 0) {
      // Extended attributes pick pattern data from CHR ROM; a CHR-RAM board
      // keeps the ordinary fetch.
      return null;
    }

    var pos = ((coarseY & 31) << 5) | (coarseX & 31);
    var byte = this.exRam[pos];
    // Bits 5-0 with $5130 on top give the 4 KB bank of the tile; the PPU's
    // pattern table select ($2000 bit 4) is part of what gets ignored.
    var bank = this.chrUpper * 64 + (byte & 0x3f);
    return {
      tile: rom.vromTile[bank % rom.vromCount][tileIndex & 0xff],
      // The palette bits arrive pre-shifted to the attribute slots the
      // renderer adds the pixel column index to.
      att: ((byte >> 6) & 3) << 2,
    };
  };

  // ---- PRG banking ----

  // Decodes the PRG window a CPU address in $6000-$FFFF belongs to, leaving the
  // answer in `prgWindowRom` (true for PRG-ROM, false for work RAM),
  // `prgWindowBank` (the register's contribution to the 8 KB bank number) and
  // `prgWindowShift` (how many low bits of that number the CPU address supplies).
  Mappers[5].prototype.decodePrgWindow = function (address) {
    var reg, shift;

    if (address < 0x8000) {
      // $5113 always maps work RAM and its bit 7 is ignored.
      this.prgWindowRom = false;
      this.prgWindowBank = this.prgReg5113 & 0x07;
      this.prgWindowShift = 0;
      return;
    }

    switch (this.prgMode) {
      case 0:
        // One 32 KB ROM bank: bits 6..2 of $5117 select it, bits 1..0 come from
        // the CPU address.
        this.prgWindowRom = true;
        this.prgWindowBank = this.prgReg5117 & 0x7c;
        this.prgWindowShift = 2;
        return;

      case 1:
        if (address >= 0xc000) {
          // $C000-$FFFF is a 16 KB ROM bank from $5117, which is never RAM.
          this.prgWindowRom = true;
          this.prgWindowBank = this.prgReg5117 & 0x7e;
          this.prgWindowShift = 1;
          return;
        }
        reg = this.prgReg5115;
        shift = 1;
        break;

      case 2:
        if (address >= 0xe000) {
          this.prgWindowRom = true;
          this.prgWindowBank = this.prgReg5117 & 0x7f;
          this.prgWindowShift = 0;
          return;
        }
        if (address >= 0xc000) {
          reg = this.prgReg5116;
          shift = 0;
          break;
        }
        reg = this.prgReg5115;
        shift = 1;
        break;

      default:
        if (address >= 0xe000) {
          this.prgWindowRom = true;
          this.prgWindowBank = this.prgReg5117 & 0x7f;
          this.prgWindowShift = 0;
          return;
        }
        if (address < 0xa000) {
          reg = this.prgReg5114;
        } else if (address < 0xc000) {
          reg = this.prgReg5115;
        } else {
          reg = this.prgReg5116;
        }
        shift = 0;
        break;
    }

    // Bit 7 of every register but $5113 and $5117 picks ROM or work RAM.
    this.prgWindowRom = (reg & 0x80) !== 0;
    // A 16 KB register drops bit 0: CPU A13 drives PRG A13 instead.
    this.prgWindowBank = shift === 1 ? reg & 0x7e : reg & 0x7f;
    this.prgWindowShift = shift;
  };

  // The 8 KB bank number of a decoded window, with the low bits that the CPU
  // address supplies folded in.
  Mappers[5].prototype.prgWindowBankAt = function (address) {
    this.decodePrgWindow(address);
    return (
      this.prgWindowBank + ((address >> 13) & ((1 << this.prgWindowShift) - 1))
    );
  };

  Mappers[5].prototype.readPrg = function (address) {
    this.decodePrgWindow(address);
    if (this.prgWindowRom) {
      // Selected PRG-ROM was copied into CPU memory when the bank was set.
      return this.nes.cpu.mem[address];
    }
    return this.prgRam[this.workRamOffset(address)];
  };

  Mappers[5].prototype.writeWorkRam = function (address, value) {
    if (this.ramProtect1 !== 2 || this.ramProtect2 !== 1) {
      // Writes only go through with $5102 = %10 and $5103 = %01.
      return;
    }

    this.decodePrgWindow(address);
    if (this.prgWindowRom) {
      // PRG-ROM is not writable.
      return;
    }

    this.prgRam[this.workRamOffset(address)] = value;
    // The CPU window has to stay in sync, because reads of that window come from
    // CPU memory.
    this.nes.cpu.mem[address] = value;

    if (
      address < 0x8000 &&
      typeof this.nes.opts.onBatteryRamWrite === "function"
    ) {
      this.nes.opts.onBatteryRamWrite(address, value);
    }
  };

  Mappers[5].prototype.workRamOffset = function (address) {
    if (address < 0x8000) {
      return this.prgWindowBank * 0x2000 + (address - 0x6000);
    }
    var bank =
      this.prgWindowBank + ((address >> 13) & ((1 << this.prgWindowShift) - 1));
    return (bank * 0x2000 + (address & 0x1fff)) & 0xffff;
  };

  // Copies 8 KB of work RAM into a CPU memory window.
  Mappers[5].prototype.mapWorkRam = function (ramOffset, address) {
    for (var i = 0; i < 0x2000; i++) {
      this.nes.cpu.mem[address + i] = this.prgRam[ramOffset + i];
    }
  };

  Mappers[5].prototype.updatePrgBanks = function () {
    if (!this.nes.rom || !this.nes.rom.valid) {
      return;
    }

    // $6000-$7FFF: the work RAM page from $5113.
    this.decodePrgWindow(0x6000);
    this.mapWorkRam(this.prgWindowBank * 0x2000, 0x6000);

    // $8000-$FFFF: four 8 KB windows decoded from the current PRG mode.
    for (var i = 0; i < 4; i++) {
      var address = 0x8000 + i * 0x2000;
      var bank = this.prgWindowBankAt(address);
      if (this.prgWindowRom) {
        this.load8kRomBank(bank, address);
      } else {
        this.mapWorkRam((bank * 0x2000) & 0xffff, address);
      }
    }
  };

  // ---- CHR banking ----
  //
  // The board holds two independent CHR register sets and hands the PPU one at a
  // time: sprites read the 'A' set ($5120-$5127) and, while sprites are 8x16,
  // the background reads the 'B' set ($5128-$512B) instead. The PPU asks for the
  // set it is about to use between its background and its sprite fetches, so the
  // mapped set changes about twice per scanline.
  //
  // Decoding a set is the expensive half: it copies kilobytes of CHR ROM and
  // rewires up to 512 tile references. Doing that on every swap was measured at
  // 45% of the frame, which is what kept Castlevania III under 60 fps. Both sets
  // are therefore decoded once into their own view, and selecting one is a
  // pointer swap; a view is re-decoded only when a register it reads changes.

  // Decodes register set `set` into its view: the tile references the renderer
  // reads, plus a byte image of $0000-$1FFF for CPU reads through $2007.
  Mappers[5].prototype.buildChrView = function (set) {
    var view = this.chrView[set];
    var reg = this.chrReg;
    var i;
    var bank;
    var page;

    view.mem.fill(0);

    if (set === 1) {
      // The 'B' set only ever drives the background, and each of its registers
      // covers one page in the lower 4 KB plus the same page again in the upper
      // 4 KB.
      switch (this.chrMode) {
        case 0:
          // One 8 KB page, from $512B.
          this.view8k(view, (reg[11] & 0x3ff) * 2, 0x0000);
          break;
        case 1:
          // Two 4 KB pages, both from $512B.
          this.view4k(view, reg[11] & 0x3ff, 0x0000);
          this.view4k(view, reg[11] & 0x3ff, 0x1000);
          break;
        case 2:
          // Four 2 KB pages, from $5129 and $512B.
          this.view2k(view, reg[9] & 0x3ff, 0x0000);
          this.view2k(view, reg[9] & 0x3ff, 0x1000);
          this.view2k(view, reg[11] & 0x3ff, 0x0800);
          this.view2k(view, reg[11] & 0x3ff, 0x1800);
          break;
        default:
          // Eight 1 KB pages, from $5128-$512B.
          for (i = 0; i < 4; i++) {
            bank = reg[i + 8] & 0x3ff;
            page = i * 0x400;
            this.view1k(view, bank, page);
            this.view1k(view, bank, page + 0x1000);
          }
          break;
      }
      return;
    }

    // The 'A' set: $5101 picks the page size, and with it which registers of the
    // set are used.
    switch (this.chrMode) {
      case 0:
        // 8 KB pages: $5127.
        this.view8k(view, (reg[7] & 0x3ff) * 2, 0x0000);
        break;
      case 1:
        // 4 KB pages: $5123 and $5127.
        this.view4k(view, reg[3] & 0x3ff, 0x0000);
        this.view4k(view, reg[7] & 0x3ff, 0x1000);
        break;
      case 2:
        // 2 KB pages: $5121, $5123, $5125 and $5127.
        this.view2k(view, reg[1] & 0x3ff, 0x0000);
        this.view2k(view, reg[3] & 0x3ff, 0x0800);
        this.view2k(view, reg[5] & 0x3ff, 0x1000);
        this.view2k(view, reg[7] & 0x3ff, 0x1800);
        break;
      default:
        // 1 KB pages: $5120-$5127.
        for (i = 0; i < 8; i++) {
          this.view1k(view, reg[i] & 0x3ff, i * 0x400);
        }
        break;
    }
  };

  // The four page sizes the board maps, mirroring what load*vromBank() does for
  // a shared pattern table but writing into one view.
  Mappers[5].prototype.view1k = function (view, bank1k, address) {
    var count = this.nes.rom.vromCount;
    this.copyChrToView(
      view,
      Math.floor(bank1k / 4) % count,
      (bank1k % 4) * 1024,
      address,
      1024,
    );
  };

  Mappers[5].prototype.view2k = function (view, bank2k, address) {
    var count = this.nes.rom.vromCount;
    this.copyChrToView(
      view,
      Math.floor(bank2k / 2) % count,
      (bank2k % 2) * 2048,
      address,
      2048,
    );
  };

  Mappers[5].prototype.view4k = function (view, bank4k, address) {
    this.copyChrToView(view, bank4k % this.nes.rom.vromCount, 0, address, 4096);
  };

  Mappers[5].prototype.view8k = function (view, bank4kStart, address) {
    this.view4k(view, bank4kStart, address);
    this.view4k(view, bank4kStart + 1, address + 4096);
  };

  // One slice of one 4 KB bank: the bytes go to the CPU-visible image, the
  // decoded tiles to the renderer's.
  Mappers[5].prototype.copyChrToView = function (
    view,
    bank4k,
    offset,
    address,
    bytes,
  ) {
    var rom = this.nes.rom;
    var vromTile = rom.vromTile[bank4k];
    var tileBase = offset >> 4;
    var destTile = address >> 4;
    var count = bytes >> 4;
    var i;

    view.mem.set(rom.vrom[bank4k].subarray(offset, offset + bytes), address);

    for (i = 0; i < count; i++) {
      view.tiles[destTile + i] = vromTile[tileBase + i];
    }
  };

  // A register write, or a page-size change, reshapes a set, so both views are
  // marked stale and the mapped one is rebuilt straight away.
  Mappers[5].prototype.updateChrBanks = function () {
    if (!this.nes.rom || this.nes.rom.vromCount === 0) {
      // CHR-RAM: the game writes the pattern tables through $0000-$1FFF.
      return;
    }

    // The pattern data is about to change, so the scanlines already drawn --
    // whose sprites have not been composited yet -- have to be flushed with the
    // old tiles. Swapping sets does not change any tile, so only this path
    // needs it; doing it on every swap was what made the mapper slow.
    this.nes.ppu.triggerRendering();

    this.chrViewReady[0] = false;
    this.chrViewReady[1] = false;
    this.chrVramSet = -1;
    this.mapChrView(this.chrSet);
  };

  // Hands the PPU one of the two decoded sets.
  Mappers[5].prototype.mapChrView = function (set) {
    if (!this.chrViewReady[set]) {
      this.buildChrView(set);
      this.chrViewReady[set] = true;
      this.chrVramSet = -1;
    }

    this.nes.ppu.ptTile = this.chrView[set].tiles;

    // Keep the CPU's view of $0000-$1FFF on the set that is mapped in.
    if (this.chrVramSet !== set) {
      this.nes.ppu.vramMem.set(this.chrView[set].mem, 0);
      this.chrVramSet = set;
    }
  };

  // The PPU calls this between its background and sprite fetches. With 8x16
  // sprites the two read different register sets, so the mapped one is swapped.
  Mappers[5].prototype.chrBankSelect = function (which) {
    // Sprites always use the 'A' set; the background uses 'B' only while the
    // sprites are 8x16.
    var set = which === 1 || !this.sprite8x16 ? 0 : 1;

    if (set === this.chrSet) {
      return;
    }
    this.chrSet = set;
    this.mapChrView(set);
  };

  // $2000 bit 5 switches sprites to 8x16, which hands the background over to the
  // 'B' register set.
  Mappers[5].prototype.onControlReg1Write = function (value) {
    var sprite8x16 = (value & 0x20) !== 0;
    if (sprite8x16 === this.sprite8x16) {
      return;
    }

    this.sprite8x16 = sprite8x16;
    this.watchesChrFetches = sprite8x16;
    // The background is what is being set up here, so the 'A' set stays mapped
    // until the PPU asks for the 'B' one. The registers did not change, so no
    // view has to be re-decoded.
    this.chrSet = 0;
    this.mapChrView(0);
  };

  // ---- name tables ----

  // $5105 gives each of the four name table slots its own source: one of the two
  // CIRAM pages, the extended RAM, or fill mode. The PPU picks its name table
  // buffers through `ntable1`, which the mirroring normally fills in.
  Mappers[5].prototype.updateNametableMap = function () {
    var sources = [];
    for (var slot = 0; slot < 4; slot++) {
      var source = (this.nametableMap >> (slot * 2)) & 3;
      if (source === 1) {
        sources.push(1);
      } else if (source === 2) {
        // The extended RAM only backs a name table in modes %00 and %01; with the
        // CPU access modes it reads as zeros.
        sources.push(this.exRamMode < 2 ? 4 : 6);
      } else if (source === 3) {
        sources.push(5);
      } else {
        sources.push(0);
      }
    }
    this.publishNametableStores();
    this.nes.ppu.setNametableSources(sources);
  };

  // (Re)creates the name table stores the cartridge provides. The PPU rebuilds its
  // own buffers on every reset, so the mapper has to publish its own again.
  Mappers[5].prototype.publishNametableStores = function () {
    var ppu = this.nes.ppu;
    while (ppu.nameTable.length < 4) {
      ppu.nameTable.push(null);
    }
    ppu.nameTable[4] = this.createExRamNameTable();
    ppu.nameTable[5] = this.createFillNameTable();
    ppu.nameTable[6] = this.createZeroNameTable();
  };

  // A name table backed by the 1 KB of extended RAM. Tiles and attributes share
  // the same bytes, the last 64 of which hold the attributes, as on hardware.
  Mappers[5].prototype.createExRamNameTable = function () {
    var exRam = this.exRam;
    return {
      width: 32,
      height: 32,
      name: "NtExRam",
      tile: exRam,
      attrib: exRam,
      getTileIndex: function (x, y) {
        return exRam[y * 32 + x] & 0xff;
      },
      getAttrib: function (x, y) {
        var byte = exRam[0x3c0 + (y >> 2) * 8 + (x >> 2)] & 0xff;
        var shift = ((y & 2) !== 0 ? 2 : 0) + ((x & 2) !== 0 ? 1 : 0);
        return ((byte >> shift) & 3) << 2;
      },
      writeAttrib: function (index, value) {
        exRam[0x3c0 + (index & 0x3f)] = value & 0xff;
      },
      toJSON: function () {
        return { exRam: Array.prototype.slice.call(exRam) };
      },
      fromJSON: function (s) {
        if (s && s.exRam) {
          for (var i = 0; i < exRam.length; i++) {
            exRam[i] = s.exRam[i];
          }
        }
      },
    };
  };

  // Fill mode: a name table that reads as one tile, in one palette, everywhere.
  Mappers[5].prototype.createFillNameTable = function () {
    var mapper = this;
    return {
      width: 32,
      height: 32,
      name: "NtFill",
      tile: new Array(1024),
      attrib: new Array(1024),
      getTileIndex: function () {
        return mapper.fillTile;
      },
      getAttrib: function () {
        return (mapper.fillAttrib & 3) << 2;
      },
      writeAttrib: function () {},
      toJSON: function () {
        return {};
      },
      fromJSON: function () {},
    };
  };

  // What the extended RAM reads as when $5104 put it in a CPU access mode.
  Mappers[5].prototype.createZeroNameTable = function () {
    return {
      width: 32,
      height: 32,
      name: "NtZero",
      tile: new Array(1024),
      attrib: new Array(1024),
      getTileIndex: function () {
        return 0;
      },
      getAttrib: function () {
        return 0;
      },
      writeAttrib: function () {},
      toJSON: function () {
        return {};
      },
      fromJSON: function () {},
    };
  };

  // ---- scanline IRQ ----

  // NES.frame() calls this once per scanline, right after the PPU finished the
  // line. The MMC5 counts the scanlines it sees while the PPU renders, and raises
  // the IRQ at the start of the target scanline, so it fires while the previous
  // line ends.
  Mappers[5].prototype.clockIrqCounter = function () {
    var ppu = this.nes.ppu;
    // endScanline() has already advanced the PPU to the line it is starting.
    var done = ppu.scanline === 0 ? 261 : ppu.scanline - 1;

    if (done >= 240 || !this.ppuIsRendering()) {
      // The PPU stopped rendering: the in-frame signal drops, the counter resets
      // and a pending IRQ is acknowledged.
      this.clearFrame();
      return;
    }

    this.inFrame = true;
    this.scanlineCounter = done & 0xff;

    // A target of $00 never matches, but the pending flag is raised whether or not
    // the IRQ is enabled.
    if (this.irqTarget !== 0 && done + 1 === this.irqTarget) {
      this.irqPending = true;
      this.refreshCpuIrq();
    }
  };

  // Drops the in-frame signal, which also acknowledges the IRQ and resets the
  // scanline counter.
  Mappers[5].prototype.clearFrame = function () {
    this.inFrame = false;
    this.scanlineCounter = 0;
    this.irqPending = false;
    this.refreshCpuIrq();
  };

  // The MMC5 watches PPUMASK: clearing both rendering bits drops the in-frame
  // signal, and turning rendering back on restarts the scanline counter.
  Mappers[5].prototype.onMaskRegisterWrite = function (value) {
    var rendering = (value & 0x18) !== 0;
    if (!rendering || this.renderDisabled) {
      this.clearFrame();
    }
    this.renderDisabled = !rendering;
  };

  // /IRQ is the product of the enable and pending flags. This CPU latches an IRQ
  // request instead of watching a pin, so the request is dropped again when the
  // pin goes back high.
  Mappers[5].prototype.refreshCpuIrq = function () {
    var cpu = this.nes.cpu;

    if (this.irqEnabled && this.irqPending) {
      if (!this.irqOnCpu) {
        cpu.requestIrq(cpu.IRQ_NORMAL);
        this.irqOnCpu = true;
      }
      return;
    }

    if (this.irqOnCpu && cpu.irqRequested && cpu.irqType === cpu.IRQ_NORMAL) {
      cpu.irqRequested = false;
    }
    this.irqOnCpu = false;
  };

  Mappers[5].prototype.loadROM = function () {
    if (!this.nes.rom.valid) {
      throw new Error("MMC5: Invalid ROM! Unable to load.");
    }

    this.reset();

    // Power on clears both RAMs.
    this.prgRam = new Uint8Array(0x10000);
    this.exRam = new Uint8Array(0x400);

    this.loadTrainer();
    this.updatePrgBanks();
    this.updateChrBanks();
    this.updateNametableMap();

    this.resetIrqHandler();
  };

  Mappers[5].prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    s.prgMode = this.prgMode;
    s.chrMode = this.chrMode;
    s.ramProtect1 = this.ramProtect1;
    s.ramProtect2 = this.ramProtect2;
    s.exRamMode = this.exRamMode;
    s.nametableMap = this.nametableMap;
    s.fillTile = this.fillTile;
    s.fillAttrib = this.fillAttrib;
    s.prgReg5113 = this.prgReg5113;
    s.prgReg5114 = this.prgReg5114;
    s.prgReg5115 = this.prgReg5115;
    s.prgReg5116 = this.prgReg5116;
    s.prgReg5117 = this.prgReg5117;
    s.chrReg = this.chrReg.slice();
    s.chrUpper = this.chrUpper;
    s.splitControl = this.splitControl;
    s.splitScroll = this.splitScroll;
    s.splitBank = this.splitBank;
    s.irqTarget = this.irqTarget;
    s.irqEnabled = this.irqEnabled;
    s.irqPending = this.irqPending;
    s.inFrame = this.inFrame;
    s.scanlineCounter = this.scanlineCounter;
    s.multA = this.multA;
    s.multB = this.multB;
    s.prgRam = Array.prototype.slice.call(this.prgRam);
    s.exRam = Array.prototype.slice.call(this.exRam);
    if (this.sound) {
      s.sound = this.sound.toJSON();
    }
    return s;
  };

  Mappers[5].prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);
    this.prgMode = s.prgMode;
    this.chrMode = s.chrMode;
    this.ramProtect1 = s.ramProtect1;
    this.ramProtect2 = s.ramProtect2;
    this.exRamMode = s.exRamMode;
    this.nametableMap = s.nametableMap;
    this.fillTile = s.fillTile;
    this.fillAttrib = s.fillAttrib;
    this.prgReg5113 = s.prgReg5113;
    this.prgReg5114 = s.prgReg5114;
    this.prgReg5115 = s.prgReg5115;
    this.prgReg5116 = s.prgReg5116;
    this.prgReg5117 = s.prgReg5117;
    this.chrReg = s.chrReg.slice();
    this.chrUpper = s.chrUpper;
    this.splitControl = s.splitControl;
    this.splitScroll = s.splitScroll;
    this.splitBank = s.splitBank;
    this.irqTarget = s.irqTarget;
    this.irqEnabled = s.irqEnabled;
    this.irqPending = s.irqPending;
    this.inFrame = s.inFrame;
    this.scanlineCounter = s.scanlineCounter;
    this.multA = s.multA;
    this.multB = s.multB;
    if (!this.prgRam) {
      this.prgRam = new Uint8Array(0x10000);
      this.exRam = new Uint8Array(0x400);
    }
    for (var i = 0; i < this.prgRam.length; i++) {
      this.prgRam[i] = s.prgRam[i];
    }
    for (var j = 0; j < this.exRam.length; j++) {
      this.exRam[j] = s.exRam[j];
    }
    this.publishNametableStores();
    this.updatePrgBanks();
    this.updateChrBanks();
    this.updateNametableMap();
    this.refreshCpuIrq();
    if (s.sound && this.sound) {
      this.sound.fromJSON(s.sound);
    }
  };
}

module.exports = { register: register };
